import { Table, Text } from '@radix-ui/themes';
import { flexRender, type Table as TanstackTable } from '@tanstack/react-table';
import React from 'react';
import { TableColumnHeader } from './TableColumnHeader';

import { TruncatedTooltip } from '@common/components/TruncatedTooltip';
import { isEmpty } from '@common/utils/typeGuards';
import { Trans } from 'react-i18next';
import styles from './TableDisplay.module.css';
import { TableSkeletonRow } from './TableSkeletonRow';

interface TableDisplayProps<TData extends { id: string }> {
    table: TanstackTable<TData>;
    isLoading?: boolean;
    isSortingEnabled: boolean;
    isResizingEnabled: boolean;
}

export const TableDisplay = <TData extends { id: string }>({
    table,
    isLoading = false,
    isSortingEnabled,
    isResizingEnabled,
}: TableDisplayProps<TData>) => {
    const tableHeaderRef = React.useRef<HTMLTableSectionElement>(null);
    const scrollAreaRootRef = React.useRef<HTMLTableElement>(null);

    React.useEffect(() => {
        if (tableHeaderRef.current && scrollAreaRootRef.current) {
            const headerHeight = tableHeaderRef.current.offsetHeight;
            scrollAreaRootRef.current.style.setProperty(
                '--table-header-height',
                `${headerHeight}px`,
            );
        }
    }, [table]);

    const pageSize = table.getState().pagination?.pageSize;
    const visibleColumnCount = table.getVisibleFlatColumns().length;
    const rows = table.getRowModel().rows;
    const hasData = !isEmpty(rows);
    const skeletonRowCount = pageSize > 0 ? pageSize : 10;

    return (
        <Table.Root
            ref={scrollAreaRootRef}
            className={styles.tableRoot}
            layout={'fixed'}
        >
            <Table.Header className={styles.stickyHeader} ref={tableHeaderRef}>
                {table.getHeaderGroups().map((headerGroup) => (
                    <Table.Row
                        className={styles.tableHeader}
                        key={headerGroup.id}
                    >
                        {headerGroup.headers.map((header) => (
                            <TableColumnHeader
                                key={header.id}
                                header={header}
                                isSortingEnabled={isSortingEnabled}
                                isResizingEnabled={isResizingEnabled}
                            />
                        ))}
                    </Table.Row>
                ))}
            </Table.Header>

            <Table.Body className={styles.tableBody}>
                {isLoading ? (
                    Array.from({
                        length: skeletonRowCount,
                    }).map((_, index) => (
                        <TableSkeletonRow
                            key={index}
                            columnCount={visibleColumnCount}
                        />
                    ))
                ) : hasData ? (
                    table.getRowModel().rows.map((row) => (
                        <Table.Row key={row.id} className={styles.tableRow}>
                            {row.getVisibleCells().map((cell) => (
                                <Table.Cell
                                    key={cell.id}
                                    className={styles.tableCell}
                                >
                                    <TruncatedTooltip
                                        enabled={cell.column.id !== 'select'}
                                        width={'100%'}
                                        text={cell.getValue() as string}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </TruncatedTooltip>
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    ))
                ) : (
                    <Table.Row>
                        <Table.Cell
                            colSpan={table.getVisibleFlatColumns().length}
                            align={'center'}
                        >
                            <Text highContrast>
                                <Trans i18nKey={'table:noResult'} />
                            </Text>
                        </Table.Cell>
                    </Table.Row>
                )}
            </Table.Body>
        </Table.Root>
    );
};
